/**
 * URL validation and detection utilities for URL paste-to-reference workflow
 */

// Comprehensive URL regex pattern that matches various URL formats
const URL_REGEX = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;

// More strict URL pattern for validation
const STRICT_URL_REGEX = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;

// Pattern to detect URLs in text that might not have protocol
const URL_LIKE_PATTERN = /(?:https?:\/\/)?(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&//=]*)?/g;

/**
 * Validates if a string is a valid URL
 */
function isValidURL(string: string): boolean {
  try {
    const url = new URL(string.startsWith('http') ? string : `https://${string}`);
    return ['http:', 'https:'].includes(url.protocol);
  } catch {
    return false;
  }
}

/**
 * Normalizes a URL by adding protocol if missing
 */
function normalizeURL(url: string): string {
  const trimmed = url.trim();
  
  // If it already has a protocol, return as is
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  
  // Add https protocol as default
  return `https://${trimmed}`;
}

/**
 * Extracts URLs from text content
 */
function extractURLsFromText(text: string): string[] {
  const matches = text.match(URL_LIKE_PATTERN);
  if (!matches) return [];
  
  return matches
    .map(match => match.trim())
    .filter(match => match.length > 0)
    .map(normalizeURL)
    .filter(isValidURL);
}

/**
 * Checks if text contains a single valid URL (for paste detection)
 */
export function isSingleURL(text: string): { isURL: boolean; url?: string } {
  const trimmed = text.trim();
  
  // Check if the entire text is a URL
  const normalizedURL = normalizeURL(trimmed);
  if (isValidURL(normalizedURL)) {
    return { isURL: true, url: normalizedURL };
  }
  
  // Check if text contains exactly one URL
  const urls = extractURLsFromText(trimmed);
  if (urls.length === 1 && trimmed.replace(/\s+/g, ' ').trim() === urls[0].replace(/https?:\/\//, '')) {
    return { isURL: true, url: urls[0] };
  }
  
  return { isURL: false };
}

/**
 * Checks if a URL is likely to be a shareable web content
 * (excludes things like localhost, IP addresses, etc.)
 */
export function isShareableURL(url: string): boolean {
  try {
    const urlObj = new URL(url);
    
    // Exclude localhost and IP addresses
    if (urlObj.hostname === 'localhost' || 
        urlObj.hostname.match(/^\d+\.\d+\.\d+\.\d+$/) ||
        urlObj.hostname.includes('127.0.0.1') ||
        urlObj.hostname.includes('192.168.') ||
        urlObj.hostname.includes('10.0.')) {
      return false;
    }
    
    // Must have a proper domain
    if (!urlObj.hostname.includes('.')) {
      return false;
    }
    
    // Exclude file:// and other non-web protocols
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      return false;
    }
    
    return true;
  } catch {
    return false;
  }
}

/**
 * Gets the domain name from a URL for display purposes
 */
export function getDomainFromURL(url: string): string {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
} 